import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  UnifiedListFilterBar,
  ListSortMenu,
  ListViewSwitcher,
  APPLICANTS_BOARD_VIEW_OPTIONS,
  useRetryFitAnalysis,
  buildApplicantRankByApplicationId,
  useArtifactCanvas,
  navigateWithThreadPreservation,
  EMPLOYER_HOME_PATH,
  managedEmployerOrganisationSwitcherEnabled,
  invalidateEmployerOrganisationScopedQueries,
  useEmployerOrganisationOptional,
  useUnifiedBrowseSearch,
  applyEmployerJobApplicantsFiltersToSearchParams,
  buildApplicantSkillFacets,
  DEFAULT_EMPLOYER_JOB_APPLICANTS_FILTERS,
  filterApplicantCards,
  parseEmployerJobApplicantsFiltersFromSearchParams,
  PIPELINE_BOARD_SORT_OPTIONS,
  parsePipelineBoardSort,
  usePagePaneFillLayout,
  cn,
  buildColumnSortOrderings,
  buildPipelineSortColumns,
  isInvitedPipelineApplicant,
  isActiveApplicationStatus,
} from '@yeshuman/sdk'
import type {
  ApplicantsBoardView,
  EmployerJobApplicantsFilters,
  PipelineBoardSort,
  BoardOrderEntry,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Users } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useOpportunityApplicants,
  useChangeApplicantStage,
  useApplicantDecision,
  useBulkChangeApplicantStage,
  applyOpportunityBoardStageChangeOptimistic,
  useUpdateOpportunityBoardOrder,
} from '../../hooks/use-opportunity-applicants'
import { AnalysisRetryButton } from '../search/analysis-retry-button'
import { ApplicantsBoardKanban } from './applicants-board-kanban'
import { ApplicantsBoardList } from './applicants-board-list'
import { ApplicantsBoardSkeleton } from './applicants-board-skeleton'
import { useJobWorkspaceCandidatePanel } from '../../hooks/use-job-workspace-candidate-panel'
import { toast } from 'sonner'
import { getEmployerJobApplicantsFilterDefinitions } from '../../lib/list-filters/employer-job-applicants'
import {
  readCollapsedPipelineColumns,
  writeCollapsedPipelineColumns,
} from '../../lib/job-workspace-collapsed-stages'
export function JobWorkspaceApplicantsTab() {
  const { jobId, applicationId } = useParams<{
    jobId: string
    applicationId?: string
  }>()
  const opportunityId = jobId
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const filterDefinitions = useMemo(() => getEmployerJobApplicantsFilterDefinitions(), [])

  const filters = useMemo(
    () => parseEmployerJobApplicantsFiltersFromSearchParams(searchParams),
    [searchParams],
  )

  const updateFilters = useCallback(
    (next: EmployerJobApplicantsFilters) => {
      setSearchParams(applyEmployerJobApplicantsFiltersToSearchParams(searchParams, next), {
        replace: true,
      })
    },
    [searchParams, setSearchParams],
  )

  const { board, isLoading, isFetching, error, refetch } = useOpportunityApplicants(opportunityId)
  const {
    retryEmployer,
    retryEmployerList,
    mutatingApplicationId,
    pendingApplicationIds,
    stuckApplicationIds,
    isListPending,
  } = useRetryFitAnalysis()
  const empOrgCtx = useEmployerOrganisationOptional()
  const tenantSwitcherEmployerActive =
    managedEmployerOrganisationSwitcherEnabled() && !!empOrgCtx?.enabled
  const ctxManagedSlug = empOrgCtx?.activeOrganisationSlug?.trim() ?? ''
  const boardBusy = isLoading || isFetching
  const queryClient = useQueryClient()
  const changeStageMutation = useChangeApplicantStage(opportunityId)
  const bulkChangeStageMutation = useBulkChangeApplicantStage(opportunityId)
  const boardOrderMutation = useUpdateOpportunityBoardOrder(opportunityId)
  const decisionMutation = useApplicantDecision(opportunityId)
  const candidatePanel = useJobWorkspaceCandidatePanel(opportunityId ?? '')
  const [selectedApplicantIds, setSelectedApplicantIds] = useState<Set<string>>(() => new Set())
  const [bulkStageSlug, setBulkStageSlug] = useState('')
  const [bulkActionPending, setBulkActionPending] = useState(false)
  const [boardSortMode, setBoardSortMode] = useState<PipelineBoardSort>('score')
  const { openCanvas, closeCanvas, isOpen: canvasOpen } = useArtifactCanvas()
  const prevCanvasOpen = useRef(false)

  useEffect(() => {
    if (!tenantSwitcherEmployerActive || !opportunityId) return
    if (!location.pathname.startsWith(`/employer/jobs/${opportunityId}`)) return

    const slugMismatch =
      !!ctxManagedSlug &&
      !!board?.opportunity.organisation_slug &&
      board.opportunity.organisation_slug !== ctxManagedSlug
    const loadFailed = Boolean(!isLoading && !board && error)

    if (!slugMismatch && !loadFailed) return

    const fallback = tenantSwitcherEmployerActive ? EMPLOYER_HOME_PATH : '/employer/jobs'

    if (loadFailed) {
      toast.error('Job not found for selected organisation.')
      invalidateEmployerOrganisationScopedQueries(queryClient)
    }

    navigateWithThreadPreservation(navigate, fallback, searchParams)
  }, [
    tenantSwitcherEmployerActive,
    opportunityId,
    location.pathname,
    ctxManagedSlug,
    board,
    isLoading,
    error,
    navigate,
    searchParams,
    queryClient,
  ])

  const stages = board?.stages ?? []
  const applicants = board?.applicants ?? []
  const [boardPositionOverrides, setBoardPositionOverrides] = useState<Map<string, number> | null>(
    null,
  )

  useEffect(() => {
    setBoardPositionOverrides(null)
  }, [opportunityId])

  const boardApplicants = useMemo(() => {
    if (!boardPositionOverrides?.size) return applicants
    return applicants.map((applicant) => {
      const override = boardPositionOverrides.get(applicant.id)
      if (override === undefined) return applicant
      return { ...applicant, board_position: override }
    })
  }, [applicants, boardPositionOverrides])

  useEffect(() => {
    if (!boardPositionOverrides?.size) return
    const matchesServer = [...boardPositionOverrides.entries()].every(
      ([id, position]) => applicants.find((applicant) => applicant.id === id)?.board_position === position,
    )
    if (matchesServer) {
      setBoardPositionOverrides(null)
    }
  }, [applicants, boardPositionOverrides])

  const rankByApplicationId = useMemo(
    () => buildApplicantRankByApplicationId(boardApplicants),
    [boardApplicants],
  )

  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(() =>
    opportunityId ? readCollapsedPipelineColumns(opportunityId) : new Set(),
  )

  useEffect(() => {
    if (!opportunityId) return
    setCollapsedColumns(readCollapsedPipelineColumns(opportunityId))
  }, [opportunityId])

  const toggleColumnCollapse = useCallback(
    (columnId: string) => {
      if (!opportunityId) return
      setCollapsedColumns((previous) => {
        const next = new Set(previous)
        if (next.has(columnId)) next.delete(columnId)
        else next.add(columnId)
        writeCollapsedPipelineColumns(opportunityId, next)
        return next
      })
    },
    [opportunityId],
  )

  const skillFacets = useMemo(
    () => buildApplicantSkillFacets(boardApplicants, filters.selectedSkills),
    [boardApplicants, filters.selectedSkills],
  )

  const filterCtx = useMemo(
    () => ({
      skillFacets,
    }),
    [skillFacets],
  )

  const filteredApplicants = useMemo(
    () => filterApplicantCards(boardApplicants, filters),
    [boardApplicants, filters],
  )

  const selectableApplicants = useMemo(
    () => filteredApplicants.filter((applicant) => !isInvitedPipelineApplicant(applicant)),
    [filteredApplicants],
  )

  const movableStages = useMemo(
    () => stages.filter((stage) => !stage.is_terminal),
    [stages],
  )

  useEffect(() => {
    setSelectedApplicantIds((previous) => {
      const allowed = new Set(selectableApplicants.map((applicant) => applicant.id))
      const next = new Set([...previous].filter((id) => allowed.has(id)))
      return next.size === previous.size ? previous : next
    })
  }, [selectableApplicants])

  const toggleApplicantSelection = useCallback((applicationId: string) => {
    setSelectedApplicantIds((previous) => {
      const next = new Set(previous)
      if (next.has(applicationId)) next.delete(applicationId)
      else next.add(applicationId)
      return next
    })
  }, [])

  const selectAllApplicants = useCallback(() => {
    setSelectedApplicantIds(new Set(selectableApplicants.map((applicant) => applicant.id)))
  }, [selectableApplicants])

  const clearApplicantSelection = useCallback(() => {
    setSelectedApplicantIds(new Set())
  }, [])

  const handleOpenColumnInListView = useCallback(
    (stageSlug: string) => {
      updateFilters({
        ...filters,
        view: 'list',
        stage: stageSlug,
      })
    },
    [filters, updateFilters],
  )

  const handleBulkMoveStage = useCallback(async () => {
    if (!opportunityId || !bulkStageSlug || selectedApplicantIds.size === 0) return
    const applicationIds = [...selectedApplicantIds]
    setBulkActionPending(true)
    try {
      await bulkChangeStageMutation.mutateAsync({
        applicationIds,
        stageSlug: bulkStageSlug,
      })
      clearApplicantSelection()
      setBulkStageSlug('')
      toast.success(
        `Moved ${applicationIds.length} applicant${applicationIds.length === 1 ? '' : 's'}`,
      )
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to move applicants')
    } finally {
      setBulkActionPending(false)
    }
  }, [
    bulkChangeStageMutation,
    bulkStageSlug,
    clearApplicantSelection,
    opportunityId,
    selectedApplicantIds,
  ])

  const handleBulkReject = useCallback(async () => {
    if (!opportunityId || selectedApplicantIds.size === 0) return
    const applicationIds = [...selectedApplicantIds]
    setBulkActionPending(true)
    let failed = 0
    try {
      for (const applicationId of applicationIds) {
        try {
          await decisionMutation.mutateAsync({ applicationId, decision: 'rejected' })
        } catch {
          failed += 1
        }
      }
      clearApplicantSelection()
      if (failed === 0) {
        toast.success(
          `Rejected ${applicationIds.length} applicant${applicationIds.length === 1 ? '' : 's'}`,
        )
      } else {
        toast.error(`Rejected ${applicationIds.length - failed}; ${failed} failed`)
      }
    } finally {
      setBulkActionPending(false)
    }
  }, [clearApplicantSelection, decisionMutation, opportunityId, selectedApplicantIds])

  const sortColumns = useMemo(
    () => buildPipelineSortColumns(boardApplicants, stages),
    [boardApplicants, stages],
  )

  const { commitTextQuery, clearCommittedTextQuery } = useUnifiedBrowseSearch({
    committedQ: filters.q,
    onUpdateQ: (q) => updateFilters({ ...filters, q }),
  })

  const clearAllFilters = useCallback(() => {
    updateFilters({
      ...DEFAULT_EMPLOYER_JOB_APPLICANTS_FILTERS,
      view: filters.view,
    })
  }, [filters.view, updateFilters])

  const hasActiveFilters = useMemo(
    () =>
      filters.q.trim().length > 0 ||
      filters.stage.trim().length > 0 ||
      filterDefinitions.some((def) => def.isNonDefault(filters, filterCtx)),
    [filters, filterDefinitions, filterCtx],
  )

  const viewSwitcher = (
    <ListViewSwitcher
      value={filters.view}
      onValueChange={(view: ApplicantsBoardView) => updateFilters({ ...filters, view })}
      options={APPLICANTS_BOARD_VIEW_OPTIONS}
      ariaLabel="Applicants board view"
    />
  )

  const selectedApplicant = useMemo(
    () => boardApplicants.find((a) => a.id === applicationId) ?? null,
    [boardApplicants, applicationId],
  )

  const selectedDetailRefreshKey = useMemo(() => {
    if (!selectedApplicant) return ''
    return [
      selectedApplicant.id,
      selectedApplicant.current_stage?.slug ?? '',
      selectedApplicant.updated_at,
    ].join('|')
  }, [selectedApplicant])

  const stageSlugsKey = useMemo(
    () => (stages.length ? stages.map((s) => s.slug).join(',') : ''),
    [stages],
  )

  const boardOppKey = board?.opportunity.id ?? ''

  function applicantSubPath(appId: string | null) {
    const base = `/employer/jobs/${opportunityId}/applicants`
    const search = window.location.search
    return appId ? `${base}/${appId}${search}` : `${base}${search}`
  }

  function openApplicant(appId: string) {
    if (!opportunityId) return
    navigate(applicantSubPath(appId))
  }

  function closeApplicant() {
    navigate(applicantSubPath(null))
  }

  useEffect(() => {
    if (prevCanvasOpen.current && !canvasOpen && applicationId) {
      navigate(applicantSubPath(null), { replace: true })
    }
    prevCanvasOpen.current = canvasOpen
  }, [applicationId, canvasOpen, navigate, opportunityId])

  function handleMoveStage(appId: string, stageSlug: string) {
    if (!opportunityId) return
    const previous = applyOpportunityBoardStageChangeOptimistic(
      queryClient,
      opportunityId,
      appId,
      stageSlug,
    )
    changeStageMutation.mutate(
      { applicationId: appId, stageSlug },
      {
        onError: () => {
          if (previous) {
            queryClient.setQueryData(['opportunity-applicants', opportunityId], previous)
          }
        },
      },
    )
  }

  function handleRecordDecision(
    appId: string,
    decision: 'hired' | 'rejected' | 'offer',
    reason?: string,
  ) {
    decisionMutation.mutate({ applicationId: appId, decision, reason })
  }

  const handlePersistOrder = useCallback(
    (orderings: BoardOrderEntry[]) => {
      if (!opportunityId || orderings.length === 0) return
      setBoardPositionOverrides(
        new Map(orderings.map((entry) => [entry.application_id, entry.board_position])),
      )
      boardOrderMutation.mutate(
        { orderings },
        {
          onError: () => {
            setBoardPositionOverrides(null)
            toast.error('Failed to save board order')
          },
        },
      )
    },
    [boardOrderMutation, opportunityId],
  )

  const handleSortBoard = useCallback(
    (mode: PipelineBoardSort) => {
      const applicantById = new Map(boardApplicants.map((applicant) => [applicant.id, applicant]))
      const orderings = buildColumnSortOrderings(
        sortColumns.map((column) => ({
          items: column.items.map((item) => ({
            id: item.id,
            board_position: item.board_position,
          })),
        })),
        mode,
        (id) => applicantById.get(id)?.match_score ?? null,
        (id) => applicantById.get(id)?.applied_at ?? '',
      )
      if (orderings.length === 0) return
      handlePersistOrder(orderings)
    },
    [boardApplicants, handlePersistOrder, sortColumns],
  )

  const handlePipelineSortChange = useCallback(
    (value: string) => {
      const mode = parsePipelineBoardSort(value)
      setBoardSortMode(mode)
      handleSortBoard(mode)
    },
    [handleSortBoard],
  )

  const sortMenu =
    boardApplicants.length > 0 ? (
      <ListSortMenu
        options={PIPELINE_BOARD_SORT_OPTIONS}
        value={boardSortMode}
        onValueChange={handlePipelineSortChange}
        ariaLabel="Sort pipeline"
        disabled={boardOrderMutation.isPending}
      />
    ) : undefined

  const rerunApplicantIds = useMemo(
    () =>
      filteredApplicants
        .filter((applicant) => isActiveApplicationStatus(applicant.status))
        .filter((applicant) => !pendingApplicationIds.has(applicant.id))
        .map((applicant) => applicant.id),
    [filteredApplicants, pendingApplicationIds],
  )

  const listRerunButton =
    boardApplicants.length > 0 && opportunityId ? (
      <AnalysisRetryButton
        mode="run"
        ariaLabel="Re-run analysis for your applicants"
        disabled={rerunApplicantIds.length === 0}
        pending={isListPending}
        onClick={() => retryEmployerList(opportunityId, rerunApplicantIds)}
      />
    ) : undefined

  useEffect(() => {
    if (!applicationId) {
      closeCanvas()
      return
    }
    if (!selectedApplicant) {
      if (boardBusy) return
      closeCanvas()
      return
    }
    openCanvas({
      title: selectedApplicant.profile.name,
      content: candidatePanel.renderPanel(
        {
          kind: 'applicant',
          applicant: selectedApplicant,
          rank: rankByApplicationId.get(selectedApplicant.id),
        },
        'applicants',
      ),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, selectedDetailRefreshKey, stageSlugsKey, boardOppKey])

  useEffect(() => {
    if (!applicationId) return
    if (!board) return
    if (boardBusy) return
    if (!selectedApplicant) {
      navigate(applicantSubPath(null), { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, board, boardBusy, selectedApplicant])

  const showFilteredEmpty = boardApplicants.length > 0 && filteredApplicants.length === 0
  const analysisRetry = useMemo(
    () =>
      opportunityId
        ? {
            onRetry: (applicationId: string) => retryEmployer(applicationId, opportunityId),
            pendingIds: pendingApplicationIds,
            stuckIds: stuckApplicationIds,
            mutatingId: mutatingApplicationId,
          }
        : undefined,
    [
      mutatingApplicationId,
      opportunityId,
      pendingApplicationIds,
      retryEmployer,
      stuckApplicationIds,
    ],
  )
  const isKanbanFillLayout =
    filters.view === 'kanban' &&
    !error &&
    !showFilteredEmpty &&
    (isLoading || boardApplicants.length > 0)

  usePagePaneFillLayout(isKanbanFillLayout)

  const boardContent =
    isLoading && !board ? (
      <ApplicantsBoardSkeleton view={filters.view} className={isKanbanFillLayout ? 'h-full' : undefined} />
    ) : error ? (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-destructive">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-medium">Failed to load applicants</p>
            <p className="text-xs opacity-80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </div>
    ) : boardApplicants.length === 0 ? (
      <EmptyBoardState opportunityTitle={board?.opportunity.title} />
    ) : showFilteredEmpty ? (
      <FilteredEmptyState onClear={clearAllFilters} />
    ) : filters.view === 'kanban' ? (
      <ApplicantsBoardKanban
        className="h-full"
        stages={stages}
        applicants={filteredApplicants}
        onMoveStage={handleMoveStage}
        onPersistOrder={handlePersistOrder}
        onOpenApplicant={openApplicant}
        onRecordDecision={handleRecordDecision}
        rankByApplicationId={rankByApplicationId}
        scoresGate={undefined}
        analysisRetry={analysisRetry}
        collapsedColumns={collapsedColumns}
        onToggleColumnCollapse={toggleColumnCollapse}
        onOpenColumnInListView={handleOpenColumnInListView}
      />
    ) : (
      <ApplicantsBoardList
        stages={stages}
        applicants={filteredApplicants}
        onMoveStage={handleMoveStage}
        onOpenApplicant={openApplicant}
        onRecordDecision={handleRecordDecision}
        rankByApplicationId={rankByApplicationId}
        scoresGate={undefined}
        analysisRetry={analysisRetry}
        selectedIds={selectedApplicantIds}
        onToggleSelect={toggleApplicantSelection}
        onSelectAll={selectAllApplicants}
        onClearSelection={clearApplicantSelection}
      />
    )

  return (
    <div
      className={cn(
        isKanbanFillLayout
          ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-hidden'
          : 'space-y-4',
      )}
    >
      <div className={isKanbanFillLayout ? 'shrink-0 space-y-3' : 'space-y-3'}>
        <UnifiedListFilterBar
          listId="employerJobApplicants"
          filters={filters}
          onFiltersChange={updateFilters}
          definitions={filterDefinitions}
          ctx={filterCtx}
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          toolbar={{ viewSwitcher, sortMenu, trailingExtra: listRerunButton }}
          search={{
            searchId: 'job-applicants-search',
            searchPlaceholder: 'Search or add filters…',
            keywordDisplay: 'inline',
            onSearchBlur: commitTextQuery,
            committedSearchValue: filters.q,
            onClearCommittedSearch: clearCommittedTextQuery,
          }}
        />
        {filters.view === 'list' && selectedApplicantIds.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
            <span className="text-sm font-medium">{selectedApplicantIds.size} selected</span>
            <Select value={bulkStageSlug} onValueChange={setBulkStageSlug}>
              <SelectTrigger className="h-8 w-[180px] bg-background">
                <SelectValue placeholder="Move to stage…" />
              </SelectTrigger>
              <SelectContent>
                {movableStages.map((stage) => (
                  <SelectItem key={stage.slug} value={stage.slug}>
                    {stage.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!bulkStageSlug || bulkActionPending}
              onClick={() => void handleBulkMoveStage()}
            >
              Move
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={bulkActionPending}
              onClick={() => void handleBulkReject()}
            >
              Reject
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={bulkActionPending}
              onClick={clearApplicantSelection}
            >
              Clear
            </Button>
          </div>
        ) : null}
      </div>

      {isKanbanFillLayout ? (
        <div className="min-h-0 flex-1 overflow-hidden">{boardContent}</div>
      ) : (
        boardContent
      )}

      <div aria-hidden className="hidden">
        <Button type="button" variant="outline" tabIndex={-1} onClick={closeApplicant}>
          Close
        </Button>
      </div>
    </div>
  )
}

function EmptyBoardState({ opportunityTitle }: { opportunityTitle?: string }) {
  return (
    <div className="flex min-h-[40dvh] flex-col items-center justify-center gap-3 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Users className="h-6 w-6" />
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold">No applicants yet</h3>
        <p className="max-w-md text-sm text-muted-foreground">
          When candidates apply to {opportunityTitle ? `"${opportunityTitle}"` : 'this opportunity'}, they
          will appear here so you can move them through your pipeline.
        </p>
      </div>
    </div>
  )
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className="flex min-h-[24dvh] flex-col items-center justify-center gap-3 py-10 text-center">
      <div className="space-y-1">
        <h3 className="text-base font-semibold">No applicants match filters</h3>
        <p className="max-w-md text-sm text-muted-foreground">
          Try adjusting your filters or clear them to see the full pipeline.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onClear}>
        Clear filters
      </Button>
    </div>
  )
}
